import { LoadingScreen } from "../widgets";
import { useEffect, useState } from "react";
import { dark_mode_primary } from "../values";
import { AppMode, INTEREST_OPTIONS } from "../values";
import { auth, firestore } from "../../firebase-config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { onAuthStateChanged, updateProfile } from "firebase/auth";

export function ProfilePage({ appMode }) {
    const [bio, setBio] = useState("");
    const [role, setRole] = useState("");
    const [email, setEmail] = useState("");
    const [error, setError] = useState(null);
    const [userId, setUserId] = useState("");
    const [isPro, setIsPro] = useState(false);
    const [editBio, setEditBio] = useState("");
    const [editRole, setEditRole] = useState("");
    const [photoURL, setPhotoURL] = useState("");
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [createdAt, setCreatedAt] = useState("");
    const [interests, setInterests] = useState([]);
    const [experience, setExperience] = useState("");
    const [editIsPro, setEditIsPro] = useState(false);
    const [isStarter, setIsStarter] = useState(false);
    const [displayName, setDisplayName] = useState("");
    const [editPhotoURL, setEditPhotoURL] = useState("");
    const [editIsAdmin, setEditIsAdmin] = useState(false);
    const [editInterests, setEditInterests] = useState([]);
    const [isEnterprise, setIsEnterprise] = useState(false);
    const [editExperience, setEditExperience] = useState("");
    const [editIsStarter, setEditIsStarter] = useState(false);
    const [editDisplayName, setEditDisplayName] = useState("");
    const [editIsEnterprise, setEditIsEnterprise] = useState(false);

    useEffect(() => {
        let unsubscribe;
        setLoading(true);
        unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setUserId(user.uid);
                setEmail(user.email || "");
                setDisplayName(user.displayName || "");
                setPhotoURL(user.photoURL || "");
                setCreatedAt(
                    user.metadata?.creationTime
                        ? new Date(user.metadata.creationTime).toLocaleString("default", {
                            month: "long",
                            year: "numeric",
                        })
                        : ""
                );
                try {
                    const userDoc = await getDoc(doc(firestore, "users", user.uid));
                    if (userDoc.exists()) {
                        const data = userDoc.data();
                        setBio(data.bio || "");
                        setIsPro(!!data.isPro);
                        setIsAdmin(!!data.isAdmin);
                        setIsStarter(!!data.isStarter);
                        setIsEnterprise(!!data.isEnterprise);
                        setRole(data.role || "");
                        setExperience(data.experience || "");
                        setInterests(Array.isArray(data.interests) ? data.interests : []);
                        setEditDisplayName(user.displayName || "");
                        setEditPhotoURL(user.photoURL || "");
                        setEditBio(data.bio || "");
                        setEditIsPro(!!data.isPro);
                        setEditIsAdmin(!!data.isAdmin);
                        setEditIsStarter(!!data.isStarter);
                        setEditIsEnterprise(!!data.isEnterprise);
                        setEditRole(data.role || "");
                        setEditExperience(data.experience || "");
                        setEditInterests(Array.isArray(data.interests) ? data.interests : []);
                    }
                } catch (err) {
                    setError("Failed to fetch user data. Please try again.");
                }
            }
            setLoading(false);
        });
        return () => unsubscribe && unsubscribe();
    }, []);

    const handleInterestChange = (interest) => {
        setEditInterests((prev) =>
            prev.includes(interest)
                ? prev.filter((i) => i !== interest)
                : [...prev, interest]
        );
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            if (auth.currentUser && (displayName !== editDisplayName || photoURL !== editPhotoURL)) {
                await updateProfile(auth.currentUser, {
                    photoURL: editPhotoURL,
                    displayName: editDisplayName,
                });
                setDisplayName(editDisplayName);
                setPhotoURL(editPhotoURL);
            }
            await updateDoc(doc(firestore, "users", userId), {
                displayName: editDisplayName,
                photoURL: editPhotoURL,
                bio: editBio,
                isPro: editIsPro,
                isAdmin: editIsAdmin,
                isStarter: editIsStarter,
                isEnterprise: editIsEnterprise,
                role: editRole,
                experience: editExperience,
                interests: editInterests,
            });
            setBio(editBio);
            setIsPro(editIsPro);
            setIsAdmin(editIsAdmin);
            setIsStarter(editIsStarter);
            setIsEnterprise(editIsEnterprise);
            setRole(editRole);
            setExperience(editExperience);
            setInterests([...editInterests]);
        } catch (err) {
            setError("Failed to save changes. Please try again.");
        }
        setLoading(false);
    };

    const getInitials = (name) => {
        if (!name) return "U";
        return name.split(" ").map((n) => n[0]?.toUpperCase()).join("");
    };

    if (loading) {
        return <LoadingScreen appMode={appMode} />;
    }

    return (
        <section className={`min-h-screen ${appMode === AppMode.LIGHT ? "bg-gray-50" : dark_mode_primary}`}>
            <div className="w-full py-12">
                <div className="max-w-6xl mx-auto px-6">
                    <h1 className={`text-3xl font-semibold ${appMode === AppMode.LIGHT ? "text-gray-800" : "text-white"}`}>My Profile</h1>
                    <p className={`mt-2 ${appMode === AppMode.LIGHT ? "text-gray-600" : "text-gray-300"}`}>Manage your account information and preferences</p>
                </div>
            </div>
            <div className="max-w-6xl mx-auto px-6 py-10">
                <div className="flex flex-col lg:flex-row w-full gap-8">
                    <div className="lg:w-1/3 flex flex-col">
                        <div className={`rounded-xl shadow-sm overflow-hidden flex-grow ${appMode === AppMode.LIGHT ? "bg-white" : "bg-gray-800"}`}>
                            <div className={`h-32 ${appMode === AppMode.LIGHT ? "bg-gradient-to-r from-blue-400 to-indigo-500" : "bg-gradient-to-r from-gray-700 to-gray-600"}`}></div>
                            <div className="px-6 pb-6 -mt-16 relative">
                                <div className="relative inline-block">
                                    <div className={`w-32 h-32 rounded-full border-4 ${appMode === AppMode.LIGHT ? "border-white" : "border-gray-800"} overflow-hidden`}>
                                        {!photoURL
                                            ? <div className={`w-full h-full flex items-center justify-center ${appMode === AppMode.LIGHT ? "bg-blue-100 text-blue-500" : "bg-gray-700 text-gray-300"}`}>
                                                <span className="text-4xl font-light">{getInitials(displayName)}</span>
                                            </div>
                                            : <img src={photoURL} className="h-full w-full object-cover" alt="" />
                                        }
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <h2 className={`text-2xl font-bold ${appMode === AppMode.LIGHT ? "text-gray-800" : "text-white"}`}>{displayName}</h2>
                                    <p className={`mt-1 ${appMode === AppMode.LIGHT ? "text-gray-600" : "text-gray-300"}`}>{email}</p>
                                    <div className={`flex items-center mt-4 ${appMode === AppMode.LIGHT ? "text-gray-500" : "text-gray-400"}`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span>Member since {createdAt}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:w-2/3 flex flex-col">
                        <div className={`rounded-xl shadow-sm overflow-y-auto flex-grow ${appMode === AppMode.LIGHT ? "bg-white" : "bg-gray-800"}`}>
                            <div className="p-6 flex flex-col">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className={`text-xl font-bold ${appMode === AppMode.LIGHT ? "text-gray-800" : "text-white"}`}>Account Information</h2>
                                </div>
                                {error && <div className="mb-4 p-3 rounded bg-red-100 text-red-700 border border-red-200">{error}</div>}
                                <div className="space-y-6 flex-grow">
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${appMode === AppMode.LIGHT ? "text-gray-700" : "text-gray-300"}`}>Full Name</label>
                                        <input type="text" value={editDisplayName} onChange={(e) => setEditDisplayName(e.target.value)} placeholder={displayName} className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:outline-none transition-colors duration-200 ${appMode === AppMode.LIGHT
                                            ? "border-gray-300 focus:border-blue-500 focus:ring-blue-200 bg-white text-gray-800"
                                            : "border-gray-600 focus:border-blue-500 focus:ring-blue-800 bg-gray-700 text-white"
                                            }`}
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${appMode === AppMode.LIGHT ? "text-gray-700" : "text-gray-300"}`}>Email Address</label>
                                        <input type="email" value={email} disabled placeholder={email} className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:outline-none transition-colors duration-200 ${appMode === AppMode.LIGHT
                                            ? "border-gray-200 text-gray-400"
                                            : "border-gray-700 text-gray-400"
                                            }`}
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${appMode === AppMode.LIGHT ? "text-gray-700" : "text-gray-300"}`}>Bio</label>
                                        <textarea rows="4" value={editBio} onChange={(e) => setEditBio(e.target.value)} placeholder={bio || "Write a short bio about yourself..."} className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:outline-none transition-colors duration-200 ${appMode === AppMode.LIGHT
                                            ? "border-gray-300 focus:border-blue-500 focus:ring-blue-200 bg-white text-gray-800"
                                            : "border-gray-600 focus:border-blue-500 focus:ring-blue-800 bg-gray-700 text-white"
                                            }`}
                                        ></textarea>
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${appMode === AppMode.LIGHT ? "text-gray-700" : "text-gray-300"}`}>Role</label>
                                        <input type="text" value={editRole} onChange={(e) => setEditRole(e.target.value)} placeholder={role || "Your role (e.g., Developer, Designer)"} className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:outline-none transition-colors duration-200 ${appMode === AppMode.LIGHT
                                            ? "border-gray-300 focus:border-blue-500 focus:ring-blue-200 bg-white text-gray-800"
                                            : "border-gray-600 focus:border-blue-500 focus:ring-blue-800 bg-gray-700 text-white"
                                            }`}
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${appMode === AppMode.LIGHT ? "text-gray-700" : "text-gray-300"}`}>Experience</label>
                                        <input type="text" value={editExperience} onChange={(e) => setEditExperience(e.target.value)} placeholder={experience || "Your experience (e.g., 2 years, Student)"} className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:outline-none transition-colors duration-200 ${appMode === AppMode.LIGHT
                                            ? "border-gray-300 focus:border-blue-500 focus:ring-blue-200 bg-white text-gray-800"
                                            : "border-gray-600 focus:border-blue-500 focus:ring-blue-800 bg-gray-700 text-white"
                                            }`}
                                        />
                                    </div>
                                    <div>
                                        <label className={`block text-sm font-medium mb-2 ${appMode === AppMode.LIGHT ? "text-gray-700" : "text-gray-300"}`}>Interests</label>
                                        <div className="flex flex-wrap gap-4">
                                            {INTEREST_OPTIONS.map((option) => (
                                                <button type="button" onClick={() => handleInterestChange(option)} className={`px-4 py-2 rounded-full border transition ${editInterests.includes(option)
                                                    ? "bg-blue-600 text-white border-blue-600"
                                                    : appMode === AppMode.LIGHT
                                                        ? "bg-white text-gray-700 border-gray-300 hover:bg-blue-50"
                                                        : "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                                                    }`}
                                                >{option}
                                                </button>
                                            ))}
                                        </div>
                                        {interests.length > 0 && <div className="mt-2 text-xs text-gray-500">Current: {interests.join(", ")}</div>}
                                    </div>
                                </div>
                            </div>
                            <div className={`px-6 py-4 flex justify-end items-center border-t ${appMode === AppMode.LIGHT ? "border-gray-100" : "border-gray-700"}`}>
                                <button type="button" className={`px-4 py-2 mr-3 text-sm rounded-lg font-medium transition-colors duration-200 ${appMode === AppMode.LIGHT
                                    ? "text-gray-700 hover:bg-gray-100"
                                    : "text-gray-300 hover:bg-gray-700"
                                    }`}
                                    onClick={() => {
                                        setEditDisplayName(displayName);
                                        setEditPhotoURL(photoURL);
                                        setEditBio(bio);
                                        setEditIsPro(isPro);
                                        setEditIsAdmin(isAdmin);
                                        setEditIsStarter(isStarter);
                                        setEditIsEnterprise(isEnterprise);
                                        setEditRole(role);
                                        setEditExperience(experience);
                                        setEditInterests([...interests]);
                                    }}
                                >Cancel
                                </button>
                                <button type="button" className="px-4 py-2 text-sm rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200" onClick={handleSave} disabled={loading}>{loading ? "Saving..." : "Save Changes"}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}